export type VideoUploadDuplicateApiData = {
  message?: string
  videoId?: string
  videoUrl?: string
  fileName?: string | null
  uploadType?: string | null
}

type AxiosLikeError = {
  response?: {
    status?: number
    data?: VideoUploadDuplicateApiData
  }
}

const UPLOAD_TYPE_LABELS: Record<string, string> = {
  course: 'course lesson',
  resource: 'course resource',
  webinars: 'webinar',
  webinar: 'webinar',
}

function uploadTypeLabel(uploadType: string | null | undefined): string | undefined {
  if (!uploadType?.trim()) {
    return undefined
  }
  const key = uploadType.trim().toLowerCase()
  return UPLOAD_TYPE_LABELS[key] ?? key
}

export function parseVideoUploadDuplicateData(
  error: unknown
): VideoUploadDuplicateApiData | null {
  const axiosError = error as AxiosLikeError
  if (axiosError.response?.status !== 409) {
    return null
  }
  const data = axiosError.response?.data
  if (!data || typeof data !== 'object') {
    return null
  }
  return data
}

/** User-facing duplicate message aligned with course/webinar upload errors. */
export function formatVideoUploadDuplicateMessage(
  data: VideoUploadDuplicateApiData
): string {
  const parts: string[] = [
    data.message?.trim() || 'This video has already been uploaded',
  ]

  const contextLabel = uploadTypeLabel(data.uploadType)
  if (contextLabel) {
    parts.push(`It was previously added as a ${contextLabel}.`)
  }

  if (data.fileName?.trim()) {
    parts.push(`Existing file: ${data.fileName.trim()}.`)
  }

  if (data.videoId?.trim()) {
    parts.push(`Vimeo video ID: ${data.videoId.trim()}.`)
  }

  if (data.videoUrl?.trim()) {
    parts.push(`Preview: ${data.videoUrl.trim()}`)
  }

  return parts.join(' ')
}

export function getVideoUploadDuplicateToast(error: unknown): {
  title: string
  description: string
} | null {
  const data = parseVideoUploadDuplicateData(error)
  if (!data) {
    return null
  }

  return {
    title: 'Duplicate video',
    description: formatVideoUploadDuplicateMessage(data),
  }
}

/** Maps upload API 409 duplicate responses to a user-facing Error (blocks upload; no video assignment). */
export function throwIfVideoUploadDuplicate(error: unknown): never {
  const duplicateToast = getVideoUploadDuplicateToast(error)
  if (duplicateToast) {
    throw new Error(duplicateToast.description)
  }

  const axiosError = error as AxiosLikeError
  const data = axiosError.response?.data
  const fallbackMessage =
    data?.message ||
    (error instanceof Error ? error.message : undefined) ||
    'Video upload failed'
  throw new Error(fallbackMessage)
}
